import en from "../../locales/en";
import uk from "../../locales/uk";

export const LOCALE_STORAGE_KEY = "app_locale";
export const DEFAULT_LOCALE = "uk";

export const localeDefinitions = {
  en: {
    code: "en",
    name: "English",
    translations: en,
  },
  uk: {
    code: "uk",
    name: "Українська",
    translations: uk,
  },
};

export const supportedLocales = Object.keys(localeDefinitions);

export const getLocaleOptions = () => Object.values(localeDefinitions);

export const isSupportedLocale = (locale) =>
  Boolean(locale && localeDefinitions[locale]);

export const normalizeLocale = (locale, fallback = DEFAULT_LOCALE) =>
  isSupportedLocale(locale) ? locale : fallback;

export const getLocaleDefinition = (locale) =>
  localeDefinitions[normalizeLocale(locale)];

export const getLocaleTranslations = (locale) =>
  getLocaleDefinition(locale).translations;

export const resolveTranslationValue = (translations, key) => {
  if (!key) {
    return undefined;
  }

  return key.split(".").reduce((value, part) => {
    if (value && typeof value === "object" && part in value) {
      return value[part];
    }

    return undefined;
  }, translations);
};

export const translateLocaleKey = ({
  key,
  locale,
  fallback = "",
  fallbackLocale = DEFAULT_LOCALE,
}) => {
  const localizedValue = resolveTranslationValue(
    getLocaleTranslations(locale),
    key,
  );

  if (typeof localizedValue === "string") {
    return localizedValue;
  }

  const fallbackValue = resolveTranslationValue(
    getLocaleTranslations(fallbackLocale),
    key,
  );

  if (typeof fallbackValue === "string") {
    return fallbackValue;
  }

  return fallback || key;
};

const getStorage = (storage) => {
  if (storage) {
    return storage;
  }

  try {
    if (typeof window === "undefined") {
      return null;
    }

    return window.localStorage;
  } catch {
    return null;
  }
};

export const readStoredLocale = (
  storage = null,
  storageKey = LOCALE_STORAGE_KEY,
  fallback = DEFAULT_LOCALE,
) => {
  try {
    const targetStorage = getStorage(storage);

    if (!targetStorage) {
      return fallback;
    }

    return normalizeLocale(targetStorage.getItem(storageKey), fallback);
  } catch {
    return fallback;
  }
};

export const writeStoredLocale = (
  locale,
  storage = null,
  storageKey = LOCALE_STORAGE_KEY,
) => {
  try {
    const targetStorage = getStorage(storage);

    if (!targetStorage || !isSupportedLocale(locale)) {
      return false;
    }

    targetStorage.setItem(storageKey, locale);
    return true;
  } catch {
    return false;
  }
};
